import { useMemo, useState } from 'react';
import { CalendarRange, LayoutList, Printer, Table } from 'lucide-react';
import {
  DISPATCH_GROUPINGS,
  DISPATCH_GROUPING_LABELS,
  DEFAULT_DISPATCH_GROUPING,
  buildDispatchReport,
  filterDispatchTasks,
  normalizeRange,
} from '../../core/dispatch-report';
import type { DispatchGrouping } from '../../core/dispatch-report';
import { shiftDateKey, toDateKey } from '../../core/task-filters';
import { hasPermission } from '../../core/permissions';
import { fieldClass } from '../../components/ui/form-classes';
import { useTaskTeams } from '../tasks/use-task-teams';
import type { Task, TaskType } from '../../types/task';
import type { Contact } from '../../types/contact';
import type { Soldier, Vehicle } from '../../types/fleet';
import type { User } from '../../types/user';
import { DailyDispatchReport } from './DailyDispatchReport';
import { DISPATCH_VIEWS, DISPATCH_VIEW_LABELS, defaultDispatchViewFor } from './dispatch-views';
import type { DispatchView } from './dispatch-views';

interface ReportsPageProps {
  user: User;
  tasks: readonly Task[];
  taskTypes: readonly TaskType[];
  contacts: readonly Contact[];
  vehicles: readonly Vehicle[];
  soldiers: readonly Soldier[];
  loading: boolean;
}

/**
 * The reports screen: a date range, a grouping and the printable dispatch
 * sheet underneath. Only the filter bar is on screen — everything below it is
 * what comes out of the printer, so "שמירה כ-PDF" is the browser's own print
 * dialog and needs no second layout.
 */
export function ReportsPage({
  user,
  tasks,
  taskTypes,
  contacts,
  vehicles,
  soldiers,
  loading,
}: ReportsPageProps) {
  const [printedAt] = useState(() => Date.now());
  const today = toDateKey(printedAt);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [groupBy, setGroupBy] = useState<DispatchGrouping>(DEFAULT_DISPATCH_GROUPING);
  const [includeCancelled, setIncludeCancelled] = useState(false);
  // A phone opens on the portrait cards, a desktop on the wide sheet. Read once,
  // so a reader who switched view keeps their choice while they work.
  const [view, setView] = useState<DispatchView>(() =>
    defaultDispatchViewFor(typeof window === 'undefined' ? null : window.innerWidth),
  );

  const range = useMemo(() => normalizeRange(from, to), [from, to]);
  const inRange = useMemo(
    () => filterDispatchTasks(tasks, range, { includeCancelled }),
    [tasks, range, includeCancelled],
  );
  const taskIds = useMemo(() => inRange.map((task) => task.id), [inRange]);
  const { teams, loading: teamsLoading } = useTaskTeams(taskIds);

  const report = useMemo(
    () =>
      buildDispatchReport({
        tasks,
        teams,
        vehicles,
        soldiers,
        contacts,
        taskTypes,
        range,
        groupBy,
        includeCancelled,
        now: printedAt,
      }),
    [tasks, teams, vehicles, soldiers, contacts, taskTypes, range, groupBy, includeCancelled, printedAt],
  );

  if (!hasPermission(user, 'VIEW_REPORTS')) {
    return (
      <div className="mx-auto w-full max-w-xl bg-panel border border-line rounded-2xl p-6 text-center">
        <p className="font-semibold">אין לך הרשאה לצפות בדוחות</p>
        <p className="text-sm text-ink/60 mt-1">נדרשת הרשאת "צפייה בדוחות".</p>
      </div>
    );
  }

  const setQuickRange = (start: string, end: string) => {
    setFrom(start);
    setTo(end);
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4">
      <section data-print="hide" className="print:hidden bg-panel border border-line rounded-2xl p-4">
        <h2 className="text-sm font-semibold flex items-center gap-2 mb-3">
          <CalendarRange size={16} className="text-brand-500" aria-hidden />
          דף שילוח יומי — בחירת טווח והדפסה
        </h2>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <label className="block">
            <span className="block text-xs text-ink/50 mb-1">מתאריך</span>
            <input
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              className={fieldClass}
            />
          </label>
          <label className="block">
            <span className="block text-xs text-ink/50 mb-1">עד תאריך</span>
            <input
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              className={fieldClass}
            />
          </label>
          <label className="block">
            <span className="block text-xs text-ink/50 mb-1">קיבוץ</span>
            <select
              value={groupBy}
              onChange={(event) => setGroupBy(event.target.value as DispatchGrouping)}
              className={fieldClass}
            >
              {DISPATCH_GROUPINGS.map((option) => (
                <option key={option} value={option}>
                  {DISPATCH_GROUPING_LABELS[option]}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => window.print()}
              className="w-full h-10 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-700 flex items-center justify-center gap-2"
            >
              <Printer size={16} aria-hidden />
              הדפסה / שמירה כ-PDF
            </button>
          </div>
        </div>

        {/* The reading view: portrait orders for a phone, the wide sheet for paper. */}
        <div
          role="group"
          aria-label="תצוגת דף השילוח"
          className="mt-3 flex flex-wrap items-center gap-1.5"
        >
          {DISPATCH_VIEWS.map((option) => {
            const isActive = view === option;
            return (
              <button
                key={option}
                type="button"
                data-dispatch-view={option}
                aria-pressed={isActive}
                onClick={() => setView(option)}
                className={[
                  'h-9 px-3 rounded-xl border text-sm flex items-center gap-1.5 transition-colors',
                  isActive
                    ? 'border-brand-500 bg-brand-50 text-brand-700 font-semibold'
                    : 'border-line bg-panel text-ink/60 hover:text-ink',
                ].join(' ')}
              >
                {option === 'cards' ? (
                  <LayoutList size={15} aria-hidden />
                ) : (
                  <Table size={15} aria-hidden />
                )}
                {DISPATCH_VIEW_LABELS[option]}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-3">
          {[
            { label: 'היום', run: () => setQuickRange(today, today) },
            {
              label: 'מחר',
              run: () => setQuickRange(shiftDateKey(printedAt, 1), shiftDateKey(printedAt, 1)),
            },
            { label: 'השבוע הקרוב', run: () => setQuickRange(today, shiftDateKey(printedAt, 6)) },
            { label: 'כל התאריכים', run: () => setQuickRange('', '') },
          ].map((preset) => (
            <button
              key={preset.label}
              type="button"
              onClick={preset.run}
              className="h-8 px-3 rounded-lg border border-line text-xs hover:border-brand-300 hover:bg-brand-50"
            >
              {preset.label}
            </button>
          ))}

          <label className="flex items-center gap-2 text-xs text-ink/60 ms-auto">
            <input
              type="checkbox"
              checked={includeCancelled}
              onChange={(event) => setIncludeCancelled(event.target.checked)}
            />
            כלול משימות שבוטלו
          </label>
        </div>
      </section>

      {loading || teamsLoading ? (
        <p className="text-sm text-ink/60">טוען את דף השילוח…</p>
      ) : (
        <DailyDispatchReport
          report={report}
          printedBy={user.fullName}
          printedAt={printedAt}
          view={view}
        />
      )}
    </div>
  );
}
